import BaseApi from '@/core/api/baseApi'
import type { Project, ProjectAccess, CreateProjectResponse, RegenerateApiKeyResponse, ProjectDetailResponse } from '@/core/types/api'
import type { ThemeConfig } from '@/core/stores/useThemeStore'

export default class ProjectsApi extends BaseApi {
  async getProjects(search?: string): Promise<{ project: Project, permissions: string[] }[]> {
    const params: Record<string, string> = {}
    if (search) {
      params.search = search
    }
    return this.get<{ project: Project, permissions: string[] }[]>('/api-admin/projects', { params })
  }

  async getProject(id: string): Promise<ProjectDetailResponse> {
    return this.get<ProjectDetailResponse>(`/api-admin/projects/${id}`)
  }

  async createProject(
    project: Partial<Project>
  ): Promise<CreateProjectResponse> {
    return this.post<CreateProjectResponse>('/api-admin/projects', project)
  }

  async updateProject(
    id: string,
    project: Partial<Project>
  ): Promise<Project> {
    return this.patch<Project>(`/api-admin/projects/${id}`, project)
  }

  async regenerateApiKey(
    id: string,
    options?: { apiKeyExpiresAt?: string | null }
  ): Promise<RegenerateApiKeyResponse> {
    return this.post<RegenerateApiKeyResponse>(`/api-admin/projects/${id}/api-key`, options || {})
  }
  
  async deleteProject(id: string): Promise<void> {
    return this.delete(`/api-admin/projects/${id}`)
  }

  async getProjectUsers(id: string): Promise<ProjectAccess[]> {
    return this.get<ProjectAccess[]>(`/api-admin/projects/${id}/users`)
  }

  async grantAccess(
    projectId: string,
    userId: string,
    permissionsOrAccessLevel: string[] | string
  ): Promise<ProjectAccess> {
    // If it's an array, it's permissions
    if (Array.isArray(permissionsOrAccessLevel)) {
      return this.post<ProjectAccess>(`/api-admin/projects/${projectId}/access`, {
        userId,
        permissions: permissionsOrAccessLevel
      });
    }

    // If it's a string, it's the old accessLevel format
    return this.post<ProjectAccess>(`/api-admin/projects/${projectId}/access`, {
      userId,
      accessLevel: permissionsOrAccessLevel
    });
  }

  async removeAccess(projectId: string, userId: string): Promise<void> {
    return this.delete(`/api-admin/projects/${projectId}/access/${userId}`)
  }
  
  // Method for updating project theme configuration - compatibility with API
  async updateTheme(id: string, themeConfig: ThemeConfig): Promise<Project> {
    const sanitizedTheme = JSON.parse(JSON.stringify(themeConfig)) as ThemeConfig

    return this.patch<Project>(`/api-admin/projects/${id}`, {
      themeConfig: sanitizedTheme
    })
  }
}

export const projectsApi = BaseApi.createApiInstance(ProjectsApi)()
